'use client';

import { automationId } from '@terra/shared-utils';
import { Tabs } from '../../components/tabs';
import { LabelDefault } from '../../components/typography';

export type ListPageTabOption<TValue extends string> = {
  value: TValue;
  label: string;
  dataTestid?: string;
};

export type ListPageTabsProps<TValue extends string> = {
  options: ReadonlyArray<ListPageTabOption<TValue>>;
  value: TValue;
  onValueChange: (value: TValue) => void;
};

export function ListPageTabs<TValue extends string>({
  options,
  value,
  onValueChange,
}: ListPageTabsProps<TValue>) {
  return (
    <Tabs.Root
      value={value}
      onValueChange={(details) => {
        const selectedOption = options.find(
          (option) => option.value === details.value,
        );

        if (selectedOption) {
          onValueChange(selectedOption.value);
        }
      }}
      size="sm"
      variant="line"
      w="full"
    >
      <Tabs.List
        justifyContent="flex-start"
        gap="0"
        boxShadow="none"
        borderBottomWidth="1px"
        borderColor="border.neutral.subtle"
      >
        {options.map((option) => (
          <Tabs.Trigger
            key={option.value}
            value={option.value}
            px="2.5"
            pt="1"
            pb="2.5"
            color="fg.neutral.muted"
            _selected={{ color: 'fg.neutral.default' }}
            {...automationId(option.dataTestid ?? '')}
          >
            <LabelDefault as="span" color="inherit">
              {option.label}
            </LabelDefault>
          </Tabs.Trigger>
        ))}
        <Tabs.Indicator />
      </Tabs.List>
    </Tabs.Root>
  );
}
